/**
 * Integration point: this is where feature workstreams are plugged into the
 * shell. Imported once, for its side effects, from `App.tsx`.
 *
 * Handlers are registered here rather than inside components so a ribbon
 * button works regardless of which tab is currently mounted.
 */

import { DWI_STAGES, PWI_STAGES, type DwiStage, type PwiStage } from "@/ipc/contract";
import { importSubjectDir, registerPipelineActions } from "@/pipeline/actions";
import { parentDir } from "@/pipeline/paths";
import { usePipelineSession } from "@/pipeline/session";
import { followCompletedStage } from "@/viewer/follow";
import { ExportPanel } from "@/panels/ExportPanel";
import { StudyInspector } from "@/tabs/StudyWorkspace";
import { JobsPanel } from "@/panels/JobsPanel";
import { PipelineBrowser } from "@/panels/PipelineBrowser";
import { PipelineProperties } from "@/panels/PipelineProperties";
import { QcPanel } from "@/panels/QcPanel";
import { ConsolePanel } from "@/panels/ConsolePanel";
import { StudyExplorer } from "@/panels/StudyExplorer";
import { WORKSTREAM_F_ACTIONS } from "@/results/actions";
import { setShellBridge, useResultsStore } from "@/results/state";
import { dispatchAction, registerAction } from "@/shell/actions";
import { requestCommandPalette } from "@/shell/CommandPalette";
import { openHelpOverlay } from "@/shell/HelpOverlays";
import { registerDockPanel, registerTab, type TabId } from "@/shell/tabRegistry";
import { appActions, useAppStore } from "@/store/appStore";
import FileTab from "@/tabs/FileTab";
import { HomePage } from "@/nav/HomePage";
import { OutputTab } from "@/nav/OutputTab";
import { registerWorkspaceActions } from "@/nav/workspaces";
import PipelineTab from "@/tabs/PipelineTab";
import { ResultsTab } from "@/tabs/ResultsTab";
import { registerToolActions } from "@/tools/actions";
import { registerViewerActions } from "@/viewer/actions";
import { ViewportGrid } from "@/viewer/ViewportGrid";

// --------------------------------------------------------------- tabs ---

registerTab("file", { centre: FileTab });
// Home has no side panels: it is a landing surface, and the recent list
// and workspaces are already on it.
registerWorkspaceActions();

registerTab("home", { centre: HomePage });
import { DatasetTab } from "@/dataset/DatasetTab";
import { LearningTab } from "@/learning/LearningTab";
import { DwiCentre, PwiCentre } from "@/pipeline/PipelineCentre";

registerTab("dataset", { centre: DatasetTab });
registerTab("pipeline", { centre: PipelineTab, left: PipelineBrowser, right: PipelineProperties });
// The two modality workspaces: the run's ordered steps on the left, the
// images in the middle, the selected step's settings on the right. That is
// the shape this domain uses — a step list beside a viewer that never gets
// replaced — and it is what makes them different places from Study, which
// puts the file tree on the left instead.
registerTab("dwi", { centre: DwiCentre, left: PipelineBrowser, right: PipelineProperties });
registerTab("preprocess", { centre: ViewportGrid, left: StudyExplorer, right: StudyInspector });
registerTab("registration", { centre: ViewportGrid, left: StudyExplorer, right: StudyInspector });
registerTab("segmentation", { centre: ViewportGrid, left: StudyExplorer, right: StudyInspector });
registerTab("perfusion", { centre: PwiCentre, left: PipelineBrowser, right: PipelineProperties });
// Nothing is built here; the page says what is planned and what exists that
// people mistake for it. No side panels: there is nothing to browse yet.
registerTab("learning", { centre: LearningTab });
registerTab("view", { centre: ViewportGrid, left: StudyExplorer, right: StudyInspector });
registerTab("output", { centre: OutputTab });
registerTab("results", { centre: ResultsTab, right: ExportPanel });

registerDockPanel("jobs", JobsPanel);
registerDockPanel("qc", QcPanel);
registerDockPanel("console", ConsolePanel);

// ------------------------------------------------------------ actions ---

registerPipelineActions();
registerViewerActions();
registerToolActions();
for (const [id, run] of Object.entries(WORKSTREAM_F_ACTIONS)) registerAction(id, () => run());

registerAction("shell.commandPalette", () => requestCommandPalette());
registerAction("shell.shortcuts", () => openHelpOverlay("shortcuts"));
registerAction("shell.about", () => openHelpOverlay("about"));
registerAction("ribbon.pin", () => appActions.setRibbonMode("pinned"));
registerAction("ribbon.collapse", () => appActions.setRibbonMode("collapsed"));
registerAction("ribbon.autohide", () => appActions.setRibbonMode("autohide"));

// --------------------------------------------------------- shell glue ---

const TAB_IDS: TabId[] = [
  "file",
  "home",
  "pipeline",
  "preprocess",
  "registration",
  "segmentation",
  "perfusion",
  "view",
  "output",
  "results",
];
const isTabId = (id: string): id is TabId => (TAB_IDS as string[]).includes(id);

setShellBridge({
  navigateTab: (tabId) => {
    if (isTabId(tabId)) appActions.setActiveTab(tabId);
  },
  newCase: () => dispatchAction("home.newCase"),
  caseOpened: (manifest) => usePipelineSession.getState().adoptCase(manifest),
  closeCase: () => usePipelineSession.getState().closeCase(),
  importPaths: (kind, paths) => {
    const first = paths[0];
    if (!first) return;
    void importSubjectDir(kind === "folder" ? first : parentDir(first));
  },
  runStages: (stages) => {
    const session = usePipelineSession.getState();
    const kind = useAppStore.getState().currentCase?.pipelineKind ?? session.draft.pipelineKind;
    const dwi = kind === "pwi" ? [] : stages.filter((s): s is DwiStage => (DWI_STAGES as readonly string[]).includes(s));
    const pwi = kind === "dwi" ? [] : stages.filter((s): s is PwiStage => (PWI_STAGES as readonly string[]).includes(s));
    void session.run({ dwi, pwi });
    appActions.setActiveTab("pipeline");
  },
});

useResultsStore.getState().setActiveCase(useAppStore.getState().currentCase);
useAppStore.subscribe((state, prev) => {
  if (state.currentCase !== prev.currentCase) {
    useResultsStore.getState().setActiveCase(state.currentCase);
  }
});

// Stages already acted on, as `jobId:modality:stage`. A stage event can arrive
// more than once (a reconcile after a backend restart re-reads the whole job),
// and following the same stage twice would drag the view back from wherever the
// run has since got to.
const followedStages = new Set<string>();
// Runs already announced as settled, so a batch advances once per finished
// subject rather than on every subsequent store write.
const settledJobs = new Set<string>();

useAppStore.subscribe((state) => {
  // The viewer follows the run in focus only. Two runs cannot both have the
  // viewport, and the one the reader is looking at is the one that keeps it.
  const focus = state.job;
  if (focus) {
    for (const stage of focus.stages) {
      if (stage.state !== "completed") continue;
      const key = `${focus.jobId}:${stage.modality}:${stage.id}`;
      if (followedStages.has(key)) continue;
      followedStages.add(key);
      void followCompletedStage(focus.jobId, focus.caseId, stage.modality, stage.id);
    }
  }

  // Settling is watched across every run, not just the focus: a batch with two
  // subjects in flight has to notice either of them ending, or it stops feeding
  // the queue.
  for (const job of Object.values(state.jobs)) {
    if (job.state !== "completed" && job.state !== "failed" && job.state !== "cancelled") continue;
    if (settledJobs.has(job.jobId)) continue;
    settledJobs.add(job.jobId);
    usePipelineSession.getState().onJobSettled(job.state, job.jobId);
  }
});

if (import.meta.env.DEV) {
  console.info("[shell] bootstrap complete");
}

export {};
